import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BufferService, LoadingContext, M_Button, M_Table, NavigateContext } from 'src/M_Components'
import { DemurrageCalculateCost } from './demurrageCalculateCost'
import { DemurrageCheckClearance } from './demurrageCheckClearance'
import { DataTable } from 'primereact/datatable/datatable.cjs.js'
import { Column } from 'primereact/column/column.cjs.js'

export const DemurrageControlList = ({ roles }) => {
    const { t } = useTranslation()
    const ctx = useContext(LoadingContext)
    const ctx2 = useContext(NavigateContext)

    const [showGenerateInvoice, setShowGenerateInvoice] = useState(false)
    const [showCheckClearance, setShowCheckClearance] = useState(false)
    const [selectedDemurrage, setSelectedDemurrage] = useState(null)
    const [refresh, setRefresh] = useState(false)

    // Tracks the rows currently selected in the upper grid
    const [selectedRows, setSelectedRows] = useState([])

    // Lower panels — one per selected demurrage row, showing its DemurrageControl
    const [controlPanels, setControlPanels] = useState(new Map())

    // Tracks the most recently loaded control
    const [selectedControl, setSelectedControl] = useState(null)

    const demurrageControlService = new BufferService('DemurrageControl', 'secondary')

    const newRoute = (url, operation, label, row) => {
        if (operation === 'Edit' || operation === 'Copy') {
            if (row) {
                sessionStorage.setItem('editingDemurrage', JSON.stringify(row))
                sessionStorage.setItem('operationType', operation)
            } else {
                return
            }
        }
        if (operation === 'Create') ctx.setPassValues({ Operation: operation })
        ctx2.newTab(url, label)
    }

    const handleCalculateCost = (selectedRow) => {
        if (!selectedRow) {
            ctx.toastMessages('error', '', t('Please select a demurrage'))
            return
        }
        if (!selectedRow.DemurrageControlId) {
            ctx.toastMessages('error', '', t('This demurrage does not have a Demurrage Control associated'))
            return
        }
        setSelectedDemurrage(selectedRow)
        setShowGenerateInvoice(true)
    }

    const handleCalculateSuccess = (result) => {
        console.log('Cost calculated:', result)
    }

    // Edit — stores the demurrage row and navigates to DemurrageControlCrud
    const handleEditControl = (row) => {
        if (!row) return
        sessionStorage.setItem('editingDemurrage', JSON.stringify(row))
        sessionStorage.setItem('operationType', 'Edit')
        ctx2.newTab('containerControl/demurrageControlCrud', `${t('Edit')} ${t('Demurrage')}`)
    }

    // Resolves the DemurrageControl ID from a row object defensively,
    // covering all plausible field name variants the backend might return.
    const resolveDemurrageControlId = (row) =>
        row?.DemurrageControlId
        ?? row?.DemurrageControlID
        ?? row?.demurrageControlId
        ?? row?.ControlId
        ?? row?.controlId
        ?? row?.DemurrageControl?.Id
        ?? row?.DemurrageControl?.ID
        ?? null

    // Check Clearance — opens the DemurrageCheckClearance dialog for the selected row.
    // The dialog handles the API call and displays the full result table.
    const handleCheckClearance = (demurrageRow) => {
        const row = demurrageRow ?? (selectedRows.length === 1 ? selectedRows[0] : null)

        if (!row) {
            ctx.toastMessages('error', '', t('Please select a demurrage first'))
            return
        }

        if (!resolveDemurrageControlId(row)) {
            ctx.toastMessages('error', '', t('Please select a demurrage with an associated control'))
            return
        }

        setSelectedDemurrage(row)
        setShowCheckClearance(true)
    }

    // Force clearance for a specific demurrage within its associated control
    // M_Table toolbar actions may fire with undefined row — fall back to selectedRows.
    const handleForceClearance = (demurrageRow) => {
        const row = demurrageRow ?? (selectedRows.length === 1 ? selectedRows[0] : null)

        if (!row) {
            ctx.toastMessages('error', '', t('Please select a demurrage first'))
            return
        }

        const controlId = resolveDemurrageControlId(row)

        if (!controlId) {
            ctx.toastMessages('error', '', t('This demurrage does not have a Demurrage Control associated'))
            return
        }

        ctx.setIsLoad(true)
        const forceClearanceService = new BufferService(
            `DemurrageControl/${controlId}/demurrages/${row.Id}/force-clearance`,
            'secondary'
        )

        forceClearanceService.post(null)
            .then(response => {
                ctx.setIsLoad(false)
                if (response?.name === 'AxiosError') {
                    ctx.toastMessages('error', '', t('Failed to force clearance'))
                    return
                }
                ctx.toastMessages('success', '', t('Demurrage force cleared successfully'))
                setRefresh(true)
                setControlPanels(prev => {
                    const next = new Map(prev)
                    next.delete(row.Id)
                    return next
                })
                setSelectedControl(null)
            })
            .catch(() => {
                ctx.setIsLoad(false)
                ctx.toastMessages('error', '', t('Failed to force clearance'))
            })
    }

    // When rows are selected in the upper grid, load their associated DemurrageControl.
    // All panel state is rebuilt in a single setControlPanels call to avoid
    // stale state issues from multiple sequential updates.
    const handleRowSelect = (rows) => {
        if (!rows || rows.length === 0) {
            setSelectedRows([])
            setSelectedControl(null)
            return
        }
        setSelectedRows(rows)
    }

    const closePanel = (demurrageId) => {
        setControlPanels(prev => {
            const next = new Map(prev)
            next.delete(demurrageId)
            return next
        })
        setSelectedControl(null)
    }

    const formatDate = (value) => value ? value.split('T')[0] : '-'
    const formatCost = (value) => value != null ? parseFloat(value).toFixed(2) : '-'

    return (
        <>
            {/* Upper grid — Demurrage records */}
            <M_Table
                entityName={'Demurrage'}
                api='secondary'
                showTitle={true}
                title={'Demurrages'}
                roleName={'usersGroups'}
                rolePermissions={roles}
                refresh={refresh}
                setRefresh={setRefresh}
                replaceFunctions={new Map([
                    ['Create', (row) => newRoute('containerControl/demurrageControlCrud', 'Create', `${t('Add')} ${t('Demurrage')}`, row)],
                    ['Edit', (row) => newRoute('containerControl/demurrageControlCrud', 'Edit', `${t('Edit')} ${t('Demurrage')}`, row)],
                    ['Copy', (row) => newRoute('containerControl/demurrageControlCrud', 'Copy', `${t('Copy')} ${t('Demurrage')}`, row)],
                    ['GenerateInvoice', (row) => handleCalculateCost(row)],
                    ['CheckClearance', (row) => handleCheckClearance(row)],
                    ['ForceClearance', (row) => handleForceClearance(row)],
                ])}
                columnTemplates={[
                    {
                        field: 'Cost',
                        body: (rowData) => {
                            if (!rowData.Cost) return ''
                            return parseFloat(rowData.Cost).toFixed(2)
                        },
                        header: t('Cost')
                    },
                    {
                        field: 'CostPrice',
                        body: (rowData) => {
                            if (!rowData.CostPrice) return ''
                            return parseFloat(rowData.CostPrice).toFixed(2)
                        },
                        header: t('Price / Discount')
                    },
                    {
                        field: 'DemurrageControlId',
                        body: (rowData) => {
                            return rowData.DemurrageControlId ? (
                                <span style={{
                                    backgroundColor: '#4caf50',
                                    color: 'white',
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    fontSize: '0.8rem'
                                }}>
                                    {t('Has Control')}
                                </span>
                            ) : (
                                <span style={{
                                    backgroundColor: '#f44336',
                                    color: 'white',
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    fontSize: '0.8rem'
                                }}>
                                    {t('No Control')}
                                </span>
                            )
                        },
                        header: t('Control Status')
                    }
                ]}
                onPassLine={handleRowSelect}
            />

            {/*
                Lower panels — one per selected demurrage row.
                Each panel shows the DemurrageControl associated to that demurrage.
                Actions: Edit (navigates to crud), Force Clear.
            */}
            {[...controlPanels.values()].map(({ row, control, loading }) => (
                <div
                    key={row.Id}
                    style={{
                        margin: '16px',
                        border: '1px solid #dee2e6',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    }}
                >
                    {/* Panel header */}
                    <div style={{
                        padding: '12px 16px',
                        backgroundColor: '#f8f9fa',
                        borderBottom: '1px solid #dee2e6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <span style={{ fontWeight: 600, fontSize: '1rem' }}>
                            {t('Demurrage Control')} — {t('Demurrage')} {row.Id}
                        </span>
                        <span style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {control && (
                                <M_Button
                                    tooltip={t('Edit')}
                                    label={t('Edit')}
                                    onClick={() => handleEditControl(row)}
                                />
                            )}
                            <M_Button
                                tooltip={t('Force Clearance')}
                                label={t('Force Clear')}
                                onClick={() => handleForceClearance(row)}
                            />
                            <button
                                onClick={() => closePanel(row.Id)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#6c757d' }}
                            >
                                ✕
                            </button>
                        </span>
                    </div>

                    {/* Panel content */}
                    {loading ? (
                        <div style={{ padding: '24px', textAlign: 'center', color: '#6c757d' }}>
                            {t('Loading')}...
                        </div>
                    ) : !control ? (
                        <div style={{ padding: '24px', textAlign: 'center', color: '#6c757d' }}>
                            {t('No Demurrage Control associated to this demurrage')}
                        </div>
                    ) : (
                        <DataTable
                            value={[control]}
                            size="small"
                            stripedRows
                            style={{ fontSize: '0.875rem' }}
                        >
                            <Column field="Id" header={t('Control ID')} style={{ width: '80px' }} />
                            <Column field="Type" header={t('Type')} style={{ width: '100px' }} />
                            <Column
                                header={t('Date')}
                                body={(r) => formatDate(r.Date)}
                                style={{ width: '100px' }}
                            />
                            <Column field="Holidays" header={t('Holidays')} style={{ width: '80px' }} />
                            <Column
                                header={t('Cost')}
                                body={(r) => formatCost(r.DemurrageCost)}
                                style={{ width: '100px' }}
                            />
                            <Column
                                header={t('Discount')}
                                body={(r) => formatCost(r.DemurrageCostDiscount)}
                                style={{ width: '100px' }}
                            />
                            <Column field="InvoiceCompany" header={t('Invoice Company')} />
                            <Column field="InvoiceKey" header={t('Invoice Key')} style={{ width: '120px' }} />
                            <Column field="Comments" header={t('Comments')} />
                        </DataTable>
                    )}
                </div>
            ))}

            <DemurrageCalculateCost
                open={showGenerateInvoice}
                setOpen={setShowGenerateInvoice}
                selectedDemurrage={selectedDemurrage}
                onSuccess={handleCalculateSuccess}
            />

            <DemurrageCheckClearance
                open={showCheckClearance}
                setOpen={setShowCheckClearance}
                demurrage={selectedDemurrage}
            />
        </>
    )
}